import { useEffect, useMemo, useState } from "react";
import { reportToast } from "../../../../components/notifications/appToast";
import {
    createTemplate,
    deleteTemplate as deleteTemplateRequest,
    loadScopedTemplates,
    updateTemplate,
} from "../api/excelLayoutTemplates";
import { selectExcelLayoutTemplateFields } from "../mapping/excelLayoutTemplateMapping";
import { makeDefaultExcelRowLabel as defaultRowLabelOptions } from "../logic/excelLayoutDefaults";
import type {
    DataColumn,
    DataModel,
    DisplayAs,
    ExcelColumnRole,
    ExtraBreakdown,
    MetricSection,
    ModelKey,
    ReportConfig,
    ReportConfigurationExcelLayoutTemplateDto,
    SaveReportConfigurationExcelLayoutTemplatePayload,
    SectionExcelLayout,
} from "../types";
import {
    allSelectableColumnsForModel,
    breakdownColumnsForModel,
    columnMatchesKey,
    dataColumnSelectionKey,
    makeDefaultExcelLayout,
    makeMetric,
    makeReport,
    reconcileExcelLayoutColumns,
} from "../reportConfigurationLogic";
import { SelectPicker } from "./SelectPicker";
import { ValuePicker } from "./ValuePicker";
import { ExcelLayoutEditor, clearExcelLayoutTemplateCache } from "./shared/ExcelLayoutEditor";
import { DestructiveConfirmDialog } from "./DestructiveConfirmDialog";

type ExcelLayoutTemplateDefinition = Partial<SectionExcelLayout> & {
    extraBreakdowns?: ExtraBreakdown[];
};

interface GlobalTemplateConfiguratorProps {
    models: Record<ModelKey, DataModel>;
    dataModelsLoading?: boolean;
    dataModelsError?: string;
    onTemplateSaved?: () => void;
}

const METRIC_TYPE_OPTIONS: { id: DisplayAs; value: DisplayAs; name: string }[] = [
    { id: "count", value: "count", name: "Count of Records" },
    { id: "percentage", value: "percentage", name: "Percentage of Total" },
];

let globalTemplateIdSeq = 1;
const nextGlobalTemplateId = (prefix: string) => `global_${prefix}_${Date.now().toString(36)}_${globalTemplateIdSeq++}`;

function templateNameForMetric(metricLabel: string, displayAs: DisplayAs) {
    const metricType = displayAs === "percentage" ? "Percentage" : "Count";
    return `Template - ${metricType} By ${metricLabel || "Metric"}`;
}

function metricTitle(metricLabel: string, displayAs: DisplayAs) {
    return `${displayAs === "percentage" ? "Percentage By" : "Count By"} ${metricLabel || "Metric"}`;
}

function normalizeColumnName(value?: string) {
    return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function columnHasName(column: DataColumn | undefined, expected: string) {
    if (!column) return false;
    const normalized = normalizeColumnName(expected);
    return [column.label, column.key, column.sql]
        .map(normalizeColumnName)
        .some((value) => value === normalized);
}

function findBreakdownColumnByName(columns: DataColumn[], expected: string) {
    return columns.find((column) => columnHasName(column, expected));
}

function makeGlobalBreakdown(column: DataColumn, id: string): ExtraBreakdown {
    return {
        id,
        column: dataColumnSelectionKey(column),
        groups: [],
        granularity: columnHasName(column, "Month") ? "month" : undefined,
    };
}

function defaultGlobalExtraBreakdowns(modelKey: ModelKey | "", metricKey = ""): ExtraBreakdown[] {
    const columns = breakdownColumnsForModel(modelKey);
    const defaults = [
        findBreakdownColumnByName(columns, "Month"),
        findBreakdownColumnByName(columns, "Year"),
    ];

    return defaults
        .filter((column): column is DataColumn => Boolean(column))
        .filter((column) => dataColumnSelectionKey(column) !== metricKey)
        .map((column) => makeGlobalBreakdown(column, `global_${normalizeColumnName(column.label || column.key)}`));
}

function sanitizeGlobalBreakdowns(modelKey: ModelKey | "", breakdowns: ExtraBreakdown[], metricKey = "") {
    const columns = breakdownColumnsForModel(modelKey);
    const seen = new Set<string>();
    return breakdowns.reduce<ExtraBreakdown[]>((result, breakdown) => {
        const column = columns.find((item) => columnMatchesKey(item, breakdown.column));
        const key = dataColumnSelectionKey(column);
        if (!column || !key || key === metricKey || seen.has(key)) return result;
        seen.add(key);
        result.push({
            ...breakdown,
            column: key,
            granularity: columnHasName(column, "Month") ? "month" : undefined,
        });
        return result;
    }, []);
}

function normalizeTemplateName(value: string) {
    return value.trim().toLowerCase();
}

function templateDefinitionJson(section: MetricSection) {
    const layout = section.excelLayout;
    if (!layout) return "";
    const definition = selectExcelLayoutTemplateFields(layout);
    return JSON.stringify({ ...definition, extraBreakdowns: section.extraBreakdowns });
}

function templateSignature(templateName: string, section: MetricSection | null) {
    if (!section?.excelLayout) return "";
    return JSON.stringify({
        templateName: normalizeTemplateName(templateName),
        definition: templateDefinitionJson(section),
    });
}

function applyGlobalDefaultColumnRoles(section: MetricSection, layout: SectionExcelLayout): SectionExcelLayout {
    return {
        ...layout,
        enabled: true,
        mode: "pivot",
        columns: layout.columns.map((column) => {
            const extra = section.extraBreakdowns.find((item) => item.id === column.extraBreakdownId);
            const extraColumn = breakdownColumnsForModel(section.model).find((item) => columnMatchesKey(item, extra?.column || ""));
            if (extraColumn && columnHasName(extraColumn, "Year") && column.role === "ignore") {
                return {
                    ...column,
                    role: "rowLabel" as ExcelColumnRole,
                    rowLabel: defaultRowLabelOptions(column.label),
                };
            }

            return column;
        }),
    };
}

function makeGlobalReport(modelKey: ModelKey | ""): ReportConfig {
    return modelKey ? makeReport("global_template_report", "Global Template", [modelKey], false) : makeReport("global_template_report", "Global Template", [], false);
}

function buildGlobalSection(modelKey: ModelKey | "", metricColumn: DataColumn | undefined, displayAs: DisplayAs, extraBreakdowns: ExtraBreakdown[], existingLayout?: SectionExcelLayout): MetricSection {
    const metricKey = dataColumnSelectionKey(metricColumn);
    const title = metricTitle(metricColumn?.label || "", displayAs);
    const cleanBreakdowns = sanitizeGlobalBreakdowns(modelKey, extraBreakdowns, metricKey);
    const section: MetricSection = {
        ...makeMetric(modelKey, metricKey, title),
        id: "global_template_metric",
        model: modelKey,
        breakdownColumn: metricKey,
        displayAs,
        extraBreakdowns: cleanBreakdowns,
        name: title,
        nameCustom: true,
    };
    const report = makeGlobalReport(modelKey);
    const baseLayout = existingLayout
        ? reconcileExcelLayoutColumns(section, { ...existingLayout, title: existingLayout.title || title, enabled: true }, report)
        : makeDefaultExcelLayout(section, report);
    section.excelLayout = applyGlobalDefaultColumnRoles(section, {
        ...baseLayout,
        enabled: true,
        title: baseLayout.title || title,
        blockOrder: baseLayout.blockOrder || 10,
    });
    return section;
}

export function GlobalTemplateConfigurator({ models, dataModelsLoading = false, dataModelsError = "", onTemplateSaved }: GlobalTemplateConfiguratorProps) {
    const modelOptions = useMemo(() => Object.entries(models)
        .map(([key, model]) => ({ id: key, value: key, name: model.label, sortOrder: model.sortOrder ?? 0 }))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)), [models]);
    const [modelKey, setModelKey] = useState<ModelKey | "">("");
    const [metricKey, setMetricKey] = useState("");
    const [displayAs, setDisplayAs] = useState<DisplayAs | "">("");
    const [templateName, setTemplateName] = useState("");
    const [section, setSection] = useState<MetricSection | null>(null);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState<ReportConfigurationExcelLayoutTemplateDto[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [deleteTemplate, setDeleteTemplate] = useState<ReportConfigurationExcelLayoutTemplateDto | null>(null);
    const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);
    const [baselineSignature, setBaselineSignature] = useState("");
    const [pendingWorkspaceAction, setPendingWorkspaceAction] = useState<null | {
        subject: string;
        onConfirm: () => void;
    }>(null);

    const selectedModel = modelKey ? models[modelKey] : undefined;
    const metricColumns = useMemo(() => modelKey ? allSelectableColumnsForModel(modelKey) : [], [modelKey, models]);
    const selectedMetric = metricColumns.find((column) => columnMatchesKey(column, metricKey));
    const dataModelId = selectedModel?.dataModelId || 0;
    const dataModelColumnId = selectedMetric?.dataModelColumnId || 0;
    const report = useMemo(() => makeGlobalReport(modelKey), [modelKey]);
    const breakdownColumns = useMemo(() => modelKey ? breakdownColumnsForModel(modelKey) : [], [modelKey, models]);
    const selectedBreakdownKeys = useMemo(() => (section?.extraBreakdowns || []).map((item) => item.column), [section?.extraBreakdowns]);
    const breakdownOptions = useMemo(() => breakdownColumns
        .filter((column) => dataColumnSelectionKey(column) !== metricKey)
        .map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder })), [breakdownColumns, metricKey]);
    const currentSignature = useMemo(() => templateSignature(templateName, section), [templateName, section]);
    const hasTemplateChanges = !!section?.excelLayout && !!currentSignature && currentSignature !== baselineSignature;
    const saveDisabled = saving || dataModelsLoading || !dataModelId || !dataModelColumnId || !displayAs || !templateName.trim() || !section?.excelLayout || !hasTemplateChanges;
    const saveButtonLabel = selectedTemplateId ? "Update Template" : "Save Template";
    const savingButtonLabel = selectedTemplateId ? "Updating..." : "Saving...";

    async function loadGlobalTemplates() {
        if (!dataModelId || !dataModelColumnId || !displayAs) {
            setTemplates([]);
            setTemplatesError("");
            setTemplatesLoading(false);
            return;
        }

        setTemplatesLoading(true);
        setTemplatesError("");
        try {
            const response = await loadScopedTemplates(dataModelId, dataModelColumnId, displayAs, { scope: "global" });
            setTemplates(Array.isArray(response) ? response : []);
        } catch (error) {
            console.log(error);
            setTemplates([]);
            setTemplatesError("Global templates could not be loaded.");
        } finally {
            setTemplatesLoading(false);
        }
    }

    function runWithWorkspaceGuard(action: () => void, subject: string) {
        if (!hasTemplateChanges) {
            action();
            return;
        }

        setPendingWorkspaceAction({ subject, onConfirm: action });
    }

    useEffect(() => {
        if (!modelOptions.length || (modelKey && !models[modelKey])) {
            setModelKey("");
        }
    }, [modelOptions.length, modelKey, models]);

    useEffect(() => {
        if (!modelKey) {
            setMetricKey("");
            setSection(null);
            setBaselineSignature("");
            return;
        }

        if (!metricColumns.length || (metricKey && !metricColumns.some((column) => columnMatchesKey(column, metricKey)))) {
            setMetricKey("");
            setSection(null);
            setBaselineSignature("");
        }
    }, [modelKey, metricColumns, metricKey]);

    useEffect(() => {
        if (!modelKey || !selectedMetric || !displayAs) {
            setSection(null);
            setBaselineSignature("");
            return;
        }

        const defaultName = templateNameForMetric(selectedMetric.label, displayAs);
        const nextSection = buildGlobalSection(modelKey, selectedMetric, displayAs, defaultGlobalExtraBreakdowns(modelKey, dataColumnSelectionKey(selectedMetric)));
        setTemplateName(defaultName);
        setSection(nextSection);
        setBaselineSignature(templateSignature(defaultName, nextSection));
    }, [modelKey, selectedMetric?.dataModelColumnId, displayAs]);

    useEffect(() => {
        setSelectedTemplateId(null);
        loadGlobalTemplates();
    }, [dataModelId, dataModelColumnId, displayAs]);

    function updateSection(updater: (current: MetricSection) => MetricSection) {
        setSection((current) => current ? updater(current) : current);
    }

    function applyModelChange(nextModelKey: string) {
        setModelKey(nextModelKey);
        setMetricKey("");
        setDisplayAs("");
        setTemplateName("");
        setSection(null);
        setSelectedTemplateId(null);
        setBaselineSignature("");
    }

    function changeModel(nextModelKey: string) {
        if (nextModelKey === modelKey) return;
        runWithWorkspaceGuard(() => applyModelChange(nextModelKey), "Change Data Model");
    }

    function applyMetricChange(nextMetricKey: string) {
        setMetricKey(nextMetricKey);
        const nextMetric = metricColumns.find((column) => columnMatchesKey(column, nextMetricKey));
        setTemplateName(nextMetric && displayAs ? templateNameForMetric(nextMetric.label, displayAs) : "");
        setSelectedTemplateId(null);
        setBaselineSignature("");
    }

    function changeMetric(nextMetricKey: string) {
        if (nextMetricKey === metricKey) return;
        runWithWorkspaceGuard(() => applyMetricChange(nextMetricKey), "Change Metric");
    }

    function applyDisplayAsChange(nextDisplayAs: DisplayAs) {
        setDisplayAs(nextDisplayAs);
        setTemplateName(selectedMetric ? templateNameForMetric(selectedMetric.label, nextDisplayAs) : "");
        setSelectedTemplateId(null);
        setBaselineSignature("");
    }

    function changeDisplayAs(nextDisplayAs: DisplayAs) {
        if (nextDisplayAs === displayAs) return;
        runWithWorkspaceGuard(() => applyDisplayAsChange(nextDisplayAs), "Change Metric Type");
    }

    function resetToNewTemplate() {
        if (!modelKey || !selectedMetric || !displayAs) return;
        const defaultName = templateNameForMetric(selectedMetric.label, displayAs);
        const nextSection = buildGlobalSection(modelKey, selectedMetric, displayAs, defaultGlobalExtraBreakdowns(modelKey, dataColumnSelectionKey(selectedMetric)));
        setTemplateName(defaultName);
        setSection(nextSection);
        setSelectedTemplateId(null);
        setBaselineSignature(templateSignature(defaultName, nextSection));
    }

    function startNewTemplate() {
        runWithWorkspaceGuard(resetToNewTemplate, "Start New Template");
    }

    function applySelectedTemplate(templateIdValue: string) {
        if (!templateIdValue) {
            resetToNewTemplate();
            return;
        }

        const templateId = Number(templateIdValue);
        const template = templates.find((item) => item.templateId === templateId);
        if (!template || !selectedMetric || !displayAs) return;

        try {
            const parsed = JSON.parse(template.templateDefinitionJson || "{}") as ExcelLayoutTemplateDefinition;
            const { extraBreakdowns: templateBreakdowns, ...layoutDefinition } = parsed;
            const nextBreakdowns = Array.isArray(templateBreakdowns)
                ? templateBreakdowns
                : defaultGlobalExtraBreakdowns(modelKey, dataColumnSelectionKey(selectedMetric));
            const nextSection = buildGlobalSection(modelKey, selectedMetric, displayAs, nextBreakdowns, layoutDefinition as SectionExcelLayout);
            setTemplateName(template.templateName);
            setSection(nextSection);
            setSelectedTemplateId(template.templateId);
            setBaselineSignature(templateSignature(template.templateName, nextSection));
        } catch (error) {
            console.log(error);
            reportToast.warning("Template could not be loaded", "The selected template has invalid saved JSON.");
        }
    }

    function loadSelectedTemplate(templateIdValue: string) {
        if (templateIdValue === (selectedTemplateId ? String(selectedTemplateId) : "")) return;
        runWithWorkspaceGuard(() => applySelectedTemplate(templateIdValue), templateIdValue ? "Load Selected Template" : "Create New Template");
    }

    function updateBreakdownSelection(nextKeys: string[]) {
        if (!section || !displayAs) return;
        const nextBreakdowns = nextKeys
            .map((key) => {
                const existing = section.extraBreakdowns.find((breakdown) => breakdown.column === key);
                if (existing) return existing;
                const column = breakdownColumns.find((item) => columnMatchesKey(item, key));
                return column ? makeGlobalBreakdown(column, nextGlobalTemplateId("breakdown")) : null;
            })
            .filter((breakdown): breakdown is ExtraBreakdown => Boolean(breakdown));
        setSection(buildGlobalSection(modelKey, selectedMetric, displayAs, nextBreakdowns, section.excelLayout));
    }

    function toggleBreakdownColumn(key: string) {
        const selected = new Set(selectedBreakdownKeys);
        if (selected.has(key)) {
            selected.delete(key);
        } else {
            selected.add(key);
        }
        updateBreakdownSelection(Array.from(selected));
    }

    function removeBreakdown(id: string) {
        if (!section || !displayAs) return;
        const nextSection = buildGlobalSection(modelKey, selectedMetric, displayAs, section.extraBreakdowns.filter((item) => item.id !== id), section.excelLayout);
        setSection(nextSection);
    }

    async function saveTemplate() {
        if (!section?.excelLayout || !dataModelId || !dataModelColumnId || !displayAs) {
            reportToast.warning("Template is not ready", "Select a Data Model, Metric, and Metric Type before saving.");
            return;
        }

        const name = templateName.trim();
        if (!name) {
            reportToast.warning("Template name required", "Enter a template name before saving.");
            return;
        }

        setSaving(true);
        const toastId = reportToast.loading(selectedTemplateId ? "Updating global template..." : "Saving global template...", name);
        try {
            const existingTemplates = await loadScopedTemplates(dataModelId, dataModelColumnId, displayAs, { scope: "global" });
            const duplicate = existingTemplates.find((template) => template.templateId !== selectedTemplateId && normalizeTemplateName(template.templateName) === normalizeTemplateName(name));
            if (duplicate) {
                throw new Error(`Template name "${name}" already exists for this Metric and Metric Type. Please use a different name.`);
            }

            const payload: SaveReportConfigurationExcelLayoutTemplatePayload = {
                dataModelId,
                dataModelColumnId,
                reportId: null,
                displayAs,
                isGlobalTemplate: true,
                templateName: name,
                templateDefinitionJson: templateDefinitionJson(section),
            };

            const savedTemplate = selectedTemplateId
                ? await updateTemplate(selectedTemplateId, payload)
                : await createTemplate(payload);
            clearExcelLayoutTemplateCache(dataModelId, dataModelColumnId, displayAs);
            await loadGlobalTemplates();
            setSelectedTemplateId(savedTemplate?.templateId || selectedTemplateId || null);
            setTemplateName(savedTemplate?.templateName || name);
            setBaselineSignature(JSON.stringify({
                templateName: normalizeTemplateName(savedTemplate?.templateName || name),
                definition: payload.templateDefinitionJson,
            }));
            onTemplateSaved?.();
            reportToast.updateSuccess(toastId, selectedTemplateId ? "Global template updated" : "Global template saved", name);
        } catch (error) {
            console.log(error);
            reportToast.updateError(toastId, selectedTemplateId ? "Global template was not updated" : "Global template was not saved", reportToast.messageFromError(error, "Unable to save the global template."));
        } finally {
            setSaving(false);
        }
    }

    function requestDeleteSelectedTemplate() {
        if (!selectedTemplateId) return;
        const selected = templates.find((template) => template.templateId === selectedTemplateId);
        if (!selected) return;
        setDeleteTemplate(selected);
    }

    async function confirmDeleteSelectedTemplate() {
        if (!deleteTemplate || deleteTemplateLoading) return;

        setDeleteTemplateLoading(true);
        const toastId = reportToast.loading("Deleting global template...", deleteTemplate.templateName);
        try {
            await deleteTemplateRequest(deleteTemplate.templateId);
            clearExcelLayoutTemplateCache(dataModelId, dataModelColumnId, displayAs || undefined);
            resetToNewTemplate();
            await loadGlobalTemplates();
            setDeleteTemplate(null);
            onTemplateSaved?.();
            reportToast.updateSuccess(toastId, "Global template deleted", deleteTemplate.templateName);
        } catch (error) {
            console.log(error);
            reportToast.updateError(toastId, "Global template was not deleted", reportToast.messageFromError(error, "Unable to delete the global template."));
        } finally {
            setDeleteTemplateLoading(false);
        }
    }

    return (
        <div className="rc-global-template-page">
            <section className="rc-block rc-global-template-hero">
                <div>
                    <span className="rc-eyebrow">Global Template Configurator</span>
                    <h3>Reusable Excel Layout Templates</h3>
                    <p className="rc-block-hint">Create templates by Data Model, Metric, and Metric Type. Matching templates become available inside report-level Excel Layout workbook blocks.</p>
                </div>
            </section>

            {dataModelsLoading && <p className="rc-hint">Loading Data Models...</p>}
            {dataModelsError && <p className="rc-hint">{dataModelsError}</p>}

            <section className="rc-block rc-global-template-config">
                <div className="rc-global-flow">
                    <div className={modelKey ? "rc-global-flow-step complete" : "rc-global-flow-step active"}>
                        <span>1</span>
                        <div>
                            <strong>Select Data Model</strong>
                            <small>Controls which metrics and columns are available.</small>
                        </div>
                    </div>
                    <div className={metricKey ? "rc-global-flow-step complete" : modelKey ? "rc-global-flow-step active" : "rc-global-flow-step"}>
                        <span>2</span>
                        <div>
                            <strong>Select Metric</strong>
                            <small>Choose the column this template is built for.</small>
                        </div>
                    </div>
                    <div className={section ? "rc-global-flow-step complete" : selectedMetric ? "rc-global-flow-step active" : "rc-global-flow-step"}>
                        <span>3</span>
                        <div>
                            <strong>Configure Layout</strong>
                            <small>Pick metric type, name it, then adjust workbook layout.</small>
                        </div>
                    </div>
                </div>
                <div className="rc-global-template-grid">
                    <label className="rc-label-block">
                        Data Model
                        <SelectPicker options={modelOptions} value={modelKey} placeholder="Select Data Model..." searchPlaceholder="Search Data Models..." onChange={changeModel} />
                        <span className="rc-global-field-hint">Start here. The next dropdown depends on this selection.</span>
                    </label>
                    <label className="rc-label-block">
                        Metric
                        <SelectPicker options={metricColumns.map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder }))} value={metricKey} placeholder="Select Metric..." searchPlaceholder="Search Metrics..." disabled={!metricColumns.length} onChange={changeMetric} />
                        <span className="rc-global-field-hint">{modelKey ? "Now choose the metric column for this template." : "Select a Data Model first to load metrics."}</span>
                    </label>
                    <label className="rc-label-block">
                        Show As
                        <SelectPicker options={METRIC_TYPE_OPTIONS} value={displayAs} placeholder="Select Metric Type..." searchPlaceholder="Search Metric Types..." disabled={!selectedMetric} onChange={(value) => changeDisplayAs(value as DisplayAs)} />
                        <span className="rc-global-field-hint">{selectedMetric ? "Choose how the selected metric should be calculated." : "Select a Metric before choosing the metric type."}</span>
                    </label>
                    <label className="rc-label-block rc-global-template-name">
                        Template Name
                        <input value={templateName} placeholder="Enter Template Name" disabled={!selectedMetric || !displayAs} onChange={(event) => setTemplateName(event.target.value)} />
                        <span className="rc-global-field-hint">{selectedMetric && displayAs ? "Use a clear reusable name for report authors." : "Template name becomes available after Metric Type."}</span>
                    </label>
                </div>
            </section>

            {section && (
                <>
                    <section className="rc-block rc-global-breakdowns">
                        <div className="rc-section-title-row">
                            <div>
                                <h3>Breakdown Columns</h3>
                                <p className="rc-block-hint">Month, Year, and Count are prepared by default when those Data Model columns are available. Add other breakdown columns only when the template needs them.</p>
                            </div>
                        </div>
                        <div className="rc-global-breakdown-list">
                            {section.extraBreakdowns.map((breakdown) => {
                                const column = breakdownColumns.find((item) => columnMatchesKey(item, breakdown.column));
                                const label = column?.label || breakdown.column;
                                return (
                                    <div className="rc-global-breakdown-card" key={breakdown.id}>
                                        <span>{label}</span>
                                        <button type="button" className="rc-iconbtn" aria-label={`Remove ${label}`} title={`Remove ${label}`} onClick={() => removeBreakdown(breakdown.id)}>
                                            <span className="material-icons md-16" aria-hidden="true">close</span>
                                        </button>
                                    </div>
                                );
                            })}
                            {!section.extraBreakdowns.length && <p className="rc-hint">No extra breakdown columns selected.</p>}
                        </div>
                        <div className="rc-global-breakdown-picker">
                            <ValuePicker
                                mode="select"
                                options={breakdownOptions}
                                placeholder={selectedBreakdownKeys.length ? `${selectedBreakdownKeys.length} Breakdown Columns Selected` : "Select Breakdown Columns..."}
                                searchPlaceholder="Search Breakdown Columns..."
                                selectedValues={selectedBreakdownKeys}
                                selectedCount={selectedBreakdownKeys.length}
                                totalCount={breakdownOptions.length}
                                onPick={() => undefined}
                                onToggle={toggleBreakdownColumn}
                                onPickMany={(values) => updateBreakdownSelection(Array.from(new Set([...selectedBreakdownKeys, ...values])))}
                                onClear={() => updateBreakdownSelection([])}
                                disabled={!breakdownOptions.length}
                            />
                        </div>
                    </section>

                    <section className="rc-block rc-global-template-workspace">
                        <div className="rc-section-title-row rc-global-template-manager-head">
                            <div>
                                <h3>Template Workspace</h3>
                                <p className="rc-block-hint">Load an existing global template to edit it, or start a new reusable layout for the selected Data Model, Metric, and Metric Type.</p>
                            </div>
                            <div className="rc-global-template-actions">
                                <button type="button" className="secondary rc-action-button rc-action-secondary" disabled={templatesLoading} onClick={startNewTemplate}>
                                    <span className="material-icons md-18" aria-hidden="true">add</span>
                                    <span>New Template</span>
                                </button>
                                <button type="button" className="secondary rc-action-button rc-action-secondary danger" disabled={!selectedTemplateId} onClick={requestDeleteSelectedTemplate}>
                                    <span className="material-icons md-18" aria-hidden="true">delete_outline</span>
                                    <span>Delete</span>
                                </button>
                            </div>
                        </div>
                        <label className="rc-label-block rc-global-template-loader">
                            Manage Existing Global Template
                            <SelectPicker
                                options={[
                                    { id: "", value: "", name: templatesLoading ? "Loading templates..." : templates.length ? "Create new template" : "No global templates yet" },
                                    ...templates.map((template) => ({ id: String(template.templateId), value: String(template.templateId), name: template.templateName })),
                                ]}
                                value={selectedTemplateId ? String(selectedTemplateId) : ""}
                                placeholder={templatesLoading ? "Loading templates..." : templates.length ? "Create new template" : "No global templates yet"}
                                searchPlaceholder="Search Global Templates..."
                                disabled={templatesLoading}
                                onChange={loadSelectedTemplate}
                            />
                            {templatesError
                                ? <span className="rc-excel-template-error">{templatesError}</span>
                                : <span className="rc-global-field-hint">{selectedTemplateId ? "Saving updates this selected global template." : "Saving creates a new global template."}</span>}
                        </label>
                        <ExcelLayoutEditor
                            report={report}
                            section={section}
                            onChange={updateSection}
                            dataModelId={dataModelId || undefined}
                            dataModelColumnId={dataModelColumnId || undefined}
                            showTemplatePicker={false}
                            showTemplateActions={false}
                            showIncludeToggle={false}
                            showReportLayoutFields={false}
                            headingPrefix="Template Layout"
                            dataModels={models}
                        />
                    </section>

                    <section className="rc-block rc-actions rc-global-template-footer">
                        <button
                            type="button"
                            className="primary rc-action-button rc-action-primary"
                            disabled={saveDisabled}
                            onClick={saveTemplate}
                            title={saveDisabled ? (section.excelLayout && !hasTemplateChanges ? "No template changes to save." : "Complete the template details before saving.") : undefined}
                        >
                            <span className="material-icons md-18" aria-hidden="true">{saving ? "sync" : "save"}</span>
                            <span>{saving ? savingButtonLabel : saveButtonLabel}</span>
                        </button>
                    </section>
                </>
            )}
            <DestructiveConfirmDialog
                open={!!deleteTemplate}
                kind="template"
                subject={deleteTemplate?.templateName || ""}
                context={selectedModel?.label ? `${selectedModel.label}${selectedMetric?.label ? ` - ${selectedMetric.label}` : ""}` : undefined}
                impact={[
                    "This template will no longer appear in report-level Excel Layout template selections.",
                    "Reports that already saved this layout keep their current configuration.",
                ]}
                loading={deleteTemplateLoading}
                onCancel={() => !deleteTemplateLoading && setDeleteTemplate(null)}
                onConfirm={confirmDeleteSelectedTemplate}
            />
            <DestructiveConfirmDialog
                open={!!pendingWorkspaceAction}
                kind="template"
                title="Discard Unsaved Changes"
                kicker="Unsaved changes"
                description="This action will replace the current template workspace."
                subject={pendingWorkspaceAction?.subject || ""}
                context="Global Template Configurator"
                subjectIcon="edit_note"
                confirmIcon="restart_alt"
                confirmLabel="Discard Changes"
                loadingLabel="Discarding..."
                cancelLabel="Keep Editing"
                impact={[
                    "Current unsaved layout changes will be discarded.",
                    "Saved global templates are not changed unless you save them first.",
                ]}
                onCancel={() => setPendingWorkspaceAction(null)}
                onConfirm={() => {
                    const action = pendingWorkspaceAction?.onConfirm;
                    setPendingWorkspaceAction(null);
                    action?.();
                }}
            />
        </div>
    );
}
